import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Receipt, Plus, CheckCircle2, XCircle, Loader2, Search } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

const STATUS_STYLE: Record<string, string> = {
  draft:    'bg-gray-100 text-gray-600',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-600',
  refunded: 'bg-blue-100 text-blue-700',
};

const REASON_OPTIONS = [
  { value: 'billing_error',          label: 'Billing Error' },
  { value: 'service_not_rendered',   label: 'Service Not Rendered' },
  { value: 'quality_complaint',      label: 'Quality Complaint' },
  { value: 'insurance_rejection',    label: 'Insurance Rejection' },
  { value: 'double_charge',          label: 'Double Charge' },
  { value: 'other',                  label: 'Other' },
];

function fmt(n: any) {
  return '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function NewCreditNoteModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [reason, setReason] = useState('billing_error');
  const [reasonDetails, setReasonDetails] = useState('');
  const [refundMode, setRefundMode] = useState('adjusted');
  const [items, setItems] = useState([{ description: '', unit_rate: '', gst_rate: '5' }]);

  const { data: invoiceData } = useQuery({
    queryKey: ['invoice-lookup', invoiceSearch],
    queryFn: () =>
      invoiceSearch.length > 2
        ? api.get('/billing/invoices', { params: { search: invoiceSearch, limit: 10 } }).then(r => r.data)
        : Promise.resolve({ invoices: [] }),
    enabled: invoiceSearch.length > 2,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/billing/credit-notes', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-notes'] });
      onClose();
    },
  });

  function addItem() {
    setItems(i => [...i, { description: '', unit_rate: '', gst_rate: '5' }]);
  }

  function removeItem(idx: number) {
    setItems(i => i.filter((_, j) => j !== idx));
  }

  function updateItem(idx: number, key: string, val: string) {
    setItems(i => i.map((item, j) => j === idx ? { ...item, [key]: val } : item));
  }

  function buildItems() {
    return items
      .filter(i => i.description && i.unit_rate)
      .map(i => {
        const rate    = parseFloat(i.unit_rate) || 0;
        const gst     = parseFloat(i.gst_rate) || 0;
        const taxable = rate;
        const cgst    = taxable * gst / 200;  // half of GST
        const sgst    = cgst;
        return {
          description:    i.description,
          quantity:       1,
          unit_rate:      rate,
          taxable_amount: taxable,
          cgst_amount:    cgst,
          sgst_amount:    sgst,
          line_total:     taxable + cgst + sgst,
        };
      });
  }

  const builtItems  = buildItems();
  const totalAmount = builtItems.reduce((s, i) => s + i.line_total, 0);

  function handleSubmit() {
    if (!selectedInvoice || builtItems.length === 0) return;
    createMutation.mutate({
      invoice_id:     selectedInvoice.id,
      patient_id:     selectedInvoice.patient_id,
      reason,
      reason_details: reasonDetails || undefined,
      refund_mode:    refundMode,
      items:          builtItems,
    });
  }

  const invoices: any[] = invoiceData?.invoices ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-navy text-lg">New Credit Note</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-6 space-y-4">

          {/* Invoice search */}
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Search Invoice</label>
            <input
              value={invoiceSearch}
              onChange={e => { setInvoiceSearch(e.target.value); setSelectedInvoice(null); }}
              placeholder="Invoice number or patient name..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none"
            />
            {!selectedInvoice && invoices.length > 0 && (
              <div className="border border-gray-100 rounded-xl mt-1 shadow-lg max-h-40 overflow-y-auto">
                {invoices.map((inv: any) => (
                  <button
                    key={inv.id}
                    onClick={() => { setSelectedInvoice(inv); setInvoiceSearch(inv.invoice_number); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-0 border-gray-50"
                  >
                    <span className="font-medium text-navy">{inv.invoice_number}</span>
                    <span className="text-gray-400 ml-2">{inv.patient_name}</span>
                    <span className="float-right text-gray-500 font-semibold">{fmt(inv.total_amount)}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedInvoice && (
              <div className="mt-2 bg-green-50 border border-green-100 rounded-xl px-3 py-2 text-sm flex items-center gap-2">
                <CheckCircle2 size={14} className="text-green-500" />
                <span className="text-green-700 font-medium">{selectedInvoice.invoice_number}</span>
                <span className="text-gray-500">— {selectedInvoice.patient_name} — {fmt(selectedInvoice.total_amount)}</span>
              </div>
            )}
          </div>

          {/* Reason */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Reason</label>
              <select
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none"
              >
                {REASON_OPTIONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Refund Mode</label>
              <select
                value={refundMode}
                onChange={e => setRefundMode(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none"
              >
                {['adjusted','cash','upi','card','neft','cheque','dd'].map(m => (
                  <option key={m} value={m}>{m.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Details</label>
            <textarea
              value={reasonDetails}
              onChange={e => setReasonDetails(e.target.value)}
              rows={2}
              placeholder="Additional details..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none resize-none"
            />
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500">Credit Items</label>
              <button onClick={addItem} className="text-xs text-navy hover:underline flex items-center gap-1">
                <Plus size={11} /> Add Item
              </button>
            </div>
            {items.map((item, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  value={item.description}
                  onChange={e => updateItem(idx, 'description', e.target.value)}
                  placeholder="Description"
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none"
                />
                <input
                  type="number"
                  value={item.unit_rate}
                  onChange={e => updateItem(idx, 'unit_rate', e.target.value)}
                  placeholder="Amount"
                  className="w-24 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none"
                />
                <select
                  value={item.gst_rate}
                  onChange={e => updateItem(idx, 'gst_rate', e.target.value)}
                  className="w-16 border border-gray-200 rounded-xl px-2 py-2 text-xs focus:ring-2 focus:ring-navy/20 outline-none"
                >
                  {['0','5','12','18'].map(r => <option key={r} value={r}>{r}%</option>)}
                </select>
                {items.length > 1 && (
                  <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 px-1">✕</button>
                )}
              </div>
            ))}
            <div className="text-right text-sm font-semibold text-navy mt-2">
              Total Credit: {fmt(totalAmount)}
            </div>
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending || !selectedInvoice || builtItems.length === 0}
            className="px-4 py-2 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy/90 disabled:opacity-40 flex items-center gap-2"
          >
            {createMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create Credit Note
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CreditNotesPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew]         = useState(false);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['credit-notes', statusFilter],
    queryFn: () =>
      api.get('/billing/credit-notes', { params: { status: statusFilter || undefined } }).then(r => r.data),
    staleTime: 30_000,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      api.patch(`/billing/credit-notes/${id}/${action}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credit-notes'] }),
  });

  const notes: any[] = (data?.credit_notes ?? []).filter((cn: any) =>
    !search ||
    cn.credit_note_number?.toLowerCase().includes(search.toLowerCase()) ||
    cn.patient_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 max-w-screen-xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <Receipt size={22} className="text-orange" /> Credit Notes
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">Issue credits against paid or finalized invoices</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-xl text-sm hover:bg-navy/90 transition"
        >
          <Plus size={16} /> New Credit Note
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by CN number or patient..."
            className="w-full pl-8 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-navy"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 size={22} className="animate-spin mr-2" /> Loading...
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Receipt size={36} className="mx-auto mb-3 opacity-25" />
            <p className="text-sm font-medium text-navy">No credit notes found</p>
            <p className="text-xs mt-1">Credit notes are issued when a refund or billing correction is needed.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">CN Number</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Patient</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Invoice Ref</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Reason</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Amount</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {notes.map((cn: any) => (
                  <tr key={cn.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{cn.credit_note_number}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{cn.patient_name ?? '—'}</div>
                      {cn.patient_uid && <div className="text-xs text-gray-400">{cn.patient_uid}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{cn.invoice_number ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-[160px]">
                      <div className="font-medium">{REASON_OPTIONS.find(r => r.value === cn.reason)?.label ?? cn.reason}</div>
                      {cn.reason_details && <div className="text-gray-400 truncate">{cn.reason_details}</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmt(cn.total_amount)}</td>
                    <td className="px-4 py-3">
                      <span className={clsx('px-2 py-1 rounded-lg text-xs font-medium', STATUS_STYLE[cn.status] ?? 'bg-gray-100')}>
                        {cn.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {cn.created_at ? format(new Date(cn.created_at), 'dd MMM yyyy') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {cn.status === 'draft' && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => approveMutation.mutate({ id: cn.id, action: 'approve' })}
                            className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs hover:bg-green-200 transition"
                          >
                            <CheckCircle2 size={11} /> Approve
                          </button>
                          <button
                            onClick={() => approveMutation.mutate({ id: cn.id, action: 'reject' })}
                            className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-600 rounded-lg text-xs hover:bg-red-100 transition"
                          >
                            <XCircle size={11} /> Reject
                          </button>
                        </div>
                      )}
                      {cn.status === 'approved' && (
                        <button
                          onClick={() => approveMutation.mutate({ id: cn.id, action: 'refunded' })}
                          className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs hover:bg-blue-200 transition"
                        >
                          Mark Refunded
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && <NewCreditNoteModal onClose={() => setShowNew(false)} />}
    </div>
  );
}
